import { Injectable, Injector } from '@angular/core';
import { get } from '@sama/micro-dash';
import { ShapeLocationState2D } from '../../../../cross-team-shared/annotation/shared/state/shape-location-state';
import {
  KeypointShapeState,
  ShapeState2D,
} from '../../../../cross-team-shared/annotation/shared/state/shape-state';
import { vectorTaggingToolsConfig } from '../../../../cross-team-shared/tools.config';
import {
  Point2D,
  ShapeLocation,
} from '../../../../team-apis/otterdam/answers/shape-typings';
import { KeypointOptionClass } from '../../../../team-apis/otterdam/keypoint-options';
import { AnnotationOutput } from '../../../../team-apis/otterdam/outputs/annotation-output';
import { AbstractShapeTool2D } from '../shared/abstract-shape-tool-2d';
import {
  MultiClickShapeTool,
  MultiClickShapeToolHelper,
} from '../shared/tool-helpers/multi-click-shape-tool-helper';
import { KeypointSecondaryToolbarComponent } from './keypoint-secondary-toolbar.component';

@Injectable()
export class KeypointTool
  extends AbstractShapeTool2D
  implements MultiClickShapeTool
{
  readonly name = vectorTaggingToolsConfig.Keypoint.name;
  override readonly secondaryToolbarType = KeypointSecondaryToolbarComponent;

  classes: KeypointOptionClass[];

  private _selectedClass: KeypointOptionClass;

  constructor(injector: Injector) {
    super(injector);
    this.classes = getKeypointOptionClasses(this.store.state().output);
    this._selectedClass = this.classes[0];
  }

  get selectedClass() {
    return this._selectedClass;
  }

  set selectedClass(value: KeypointOptionClass) {
    this._selectedClass = value;
    this.shellConnector.trackEvent({
      id: 'change_keypoint_class',
      bucket: 'tag_selection',
      class: value.name,
    });
  }

  override async onActivate() {
    await super.onActivate();
    this.onEvent('container', 'mousedown', this.startShape);
  }

  override buildShape(initialValues: ShapeState2D): KeypointShapeState {
    return { ...initialValues, keypointClass: this.selectedClass.name };
  }

  handleInProgressMouseDown(
    helper: MultiClickShapeToolHelper,
    event: MouseEvent,
  ) {
    const pointsStore = helper.getPointsStore();
    const numPoints = pointsStore.state().length;
    helper.addPoint(pointsStore.state()[numPoints - 1]);
    if (numPoints === this.selectedClass.points.length) {
      void helper.stopListening(event);
    } else {
      helper.trackAddInProgressSubpointEvent(numPoints - 1);
    }
  }

  updateLocationToFollowMouse(location: ShapeLocation<Point2D>) {
    const numPoints = location.points.length;
    const point = this.getBoundedImagePoint();
    location.points[numPoints - 1] = point;
  }

  prepareKeyLocationForCommit(location: ShapeLocationState2D) {
    // The last location point is the in progress point under the mouse so we
    // need to remove it before committing the new shape
    location.points.splice(-1, 1);
  }

  private startShape() {
    const helper = new MultiClickShapeToolHelper(
      this,
      this.store('vectorOverlay'),
      this.injector,
    );
    this.registerHelper(helper);
  }
}

export function getKeypointOptionClasses(
  output: AnnotationOutput,
): KeypointOptionClass[] {
  return get(output.meta, ['keypoint_options', 'classes'], []);
}
